const STORAGE_KEY = 'poker-audio-v1';
export const SFX_MIX = 0.48;
export const ACTION_BUFFER_SETS = Object.freeze({
  turn: Object.freeze(['card-soft-1', 'card-soft-2', 'card-soft-3']),
  chip: Object.freeze(['button-soft-1', 'button-soft-2', 'button-soft-3'])
});

export function effectGain(name) {
  return ({ turn: 0.17, chip: 0.18, robbery: 0.34 }[name] ?? 0.18) * SFX_MIX;
}

export class AudioDirector {
  constructor() {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    this.musicEnabled = saved.musicEnabled ?? true;
    this.sfxEnabled = saved.sfxEnabled ?? true;
    this.ctx = null;
    this.musicTracks = new Map();
    this.musicMood = 'base';
    this.activeMusic = null;
    this.fadeTimer = null;
    this.unlocked = false;
    this.unlockPromise = null;
    this.buffers = new Map();
    this.variantCursor = new Map();
    this.activeSceneCues = new Set();
    this.activeSources = new Set();
    this.backgrounded = false;
  }

  unlock() {
    if (this.unlocked) return Promise.resolve();
    if (this.unlockPromise) return this.unlockPromise;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return Promise.resolve();
    try {
      this.ctx = new AudioContextClass();
    } catch {
      this.ctx = null;
      return Promise.resolve();
    }
    this.unlockPromise = this.ctx.resume().then(() => {
      this.unlocked = true;
      [
        ['base', './audio/poker-lanterns-lies-silver.mp3'],
        ['hard', './audio/poker-last-coins-on-the-felt.mp3'],
        ['high', './audio/poker-railroad-baron-stakes.mp3']
      ].forEach(([name, url]) => {
        const track = new Audio(url);
        track.loop = true;
        track.preload = 'auto';
        track.volume = 0;
        track.addEventListener('error', () => { track.dataset.failed = 'true'; }, { once: true });
        this.musicTracks.set(name, track);
      });
      this.applyMusicMood(true);
      void Promise.all([
        ...Object.values(ACTION_BUFFER_SETS).flat().map(name => this.preload(name, `./audio/sfx/${name}.mp3`)),
        this.preload('robbery', './audio/sfx/saloon-robbery.mp3')
      ]);
    }).catch(() => {
      this.ctx = null;
      this.unlocked = false;
      this.unlockPromise = null;
    });
    return this.unlockPromise;
  }

  async preload(name, url) {
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      this.buffers.set(name, await this.ctx.decodeAudioData(await response.arrayBuffer()));
    } catch { /* synthesized fallbacks remain available */ }
  }

  persist() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ musicEnabled: this.musicEnabled, sfxEnabled: this.sfxEnabled }));
  }

  setMusic(enabled) {
    this.musicEnabled = enabled;
    this.persist();
    this.applyMusicMood();
  }

  setMood(mood) {
    this.musicMood = ['base', 'hard', 'high'].includes(mood) ? mood : 'base';
    this.applyMusicMood();
  }

  async suspend() {
    this.backgrounded = true;
    clearInterval(this.fadeTimer);
    this.fadeTimer = null;
    this.musicTracks.forEach(track => {
      track.pause();
      track.volume = 0;
    });
    this.activeMusic = null;
    this.activeSources.forEach(source => {
      try { source.stop(); } catch { /* a completed source is already silent */ }
    });
    this.activeSources.clear();
    this.activeSceneCues.clear();
    if (this.ctx?.state === 'running') await this.ctx.suspend().catch(() => {});
  }

  async resume() {
    this.backgrounded = false;
    if (this.ctx && this.unlocked && this.ctx.state === 'suspended') {
      await this.ctx.resume().catch(() => {});
    }
    this.applyMusicMood(true);
  }

  applyMusicMood(immediate = false) {
    if (this.backgrounded || !this.unlocked || !this.musicTracks.size) return;
    clearInterval(this.fadeTimer);
    if (!this.musicEnabled) {
      this.musicTracks.forEach(track => { track.pause(); track.volume = 0; });
      this.activeMusic = null;
      return;
    }
    const desired = this.musicTracks.get(this.musicMood);
    const target = desired?.dataset.failed === 'true' ? this.musicTracks.get('base') : desired;
    if (!target) return;
    const previous = this.activeMusic;
    const targetVolume = { base: 0.24, hard: 0.19, high: 0.3 }[this.musicMood] ?? 0.24;
    target.play().catch(() => {});
    if (immediate || previous === target) {
      if (previous && previous !== target) { previous.pause(); previous.volume = 0; }
      target.volume = targetVolume;
      this.activeMusic = target;
      return;
    }
    target.volume = 0;
    let step = 0;
    const previousVolume = previous?.volume || 0;
    this.fadeTimer = setInterval(() => {
      step += 1;
      const progress = Math.min(1, step / 18);
      target.volume = targetVolume * progress;
      if (previous && previous !== target) previous.volume = previousVolume * (1 - progress);
      if (progress >= 1) {
        clearInterval(this.fadeTimer);
        if (previous && previous !== target) previous.pause();
        this.activeMusic = target;
      }
    }, 55);
  }

  setSfx(enabled) {
    this.sfxEnabled = enabled;
    this.persist();
  }

  tone(kind = 'chip', frequency = 220, duration = 0.08, gain = 0.055) {
    if (!this.unlocked || !this.sfxEnabled) return;
    const osc = this.ctx.createOscillator();
    const amp = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    const now = this.ctx.currentTime;
    const settings = {
      card: ['triangle', 260, 0.045], chip: ['sine', 740, 0.055], fold: ['sawtooth', 120, 0.07], win: ['triangle', 330, 0.13], robbery: ['sawtooth', 82, 0.11]
    }[kind] || ['sine', frequency, gain];
    osc.type = settings[0];
    osc.frequency.setValueAtTime(settings[1] || frequency, now);
    if (kind === 'win') osc.frequency.exponentialRampToValueAtTime(660, now + duration);
    filter.type = 'lowpass';
    filter.frequency.value = kind === 'chip' ? 2100 : 1100;
    amp.gain.setValueAtTime((settings[2] || gain) * SFX_MIX, now);
    amp.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(filter).connect(amp).connect(this.ctx.destination);
    osc.start(now);
    osc.stop(now + duration);
  }

  nextVariant(name) {
    const variants = ACTION_BUFFER_SETS[name];
    if (!variants) return name;
    const cursor = this.variantCursor.get(name) || 0;
    this.variantCursor.set(name, (cursor + 1) % variants.length);
    return variants[cursor];
  }

  playBuffer(name, mixName = name) {
    if (!this.unlocked || !this.sfxEnabled || !this.buffers.has(name)) return false;
    const isSceneCue = mixName === 'robbery';
    if (isSceneCue && this.activeSceneCues.has(mixName)) return true;
    const source = this.ctx.createBufferSource();
    const gain = this.ctx.createGain();
    source.buffer = this.buffers.get(name);
    gain.gain.value = effectGain(mixName);
    source.connect(gain).connect(this.ctx.destination);
    this.activeSources.add(source);
    if (isSceneCue) {
      this.activeSceneCues.add(mixName);
    }
    source.onended = () => {
      this.activeSources.delete(source);
      if (isSceneCue) this.activeSceneCues.delete(mixName);
    };
    source.start();
    return true;
  }

  cue(name) {
    const sourceName = name === 'card' || name === 'fold' ? 'turn' : name;
    if (this.playBuffer(this.nextVariant(sourceName), sourceName)) return;
    const timing = { turn: ['card', 330, 0.045], chip: ['chip', 520, 0.045], fold: ['card', 300, 0.045], robbery: ['robbery', 82, 0.32] }[name];
    if (timing) this.tone(...timing);
  }
}
